import { type QueryClient, mutationOptions, queryOptions } from "@tanstack/react-query";
import {
  getOperatorsHttpOrigin,
  type OperatorsAutomation,
  type OperatorsAutomationsResponse,
  type OperatorsAutomationSchedule,
  type OperatorsAutomationTarget,
} from "../operators";
import { readOperatorsAuthErrorMessage, throwOperatorsAuthExpired } from "../operatorsAuthSession";
import { OperatorsForbiddenError, operatorsQueryKeys } from "./operatorsReactQuery";

export const operatorsAutomationQueryKeys = {
  all: [...operatorsQueryKeys.all, "automations"] as const,
  list: () => [...operatorsAutomationQueryKeys.all, "list"] as const,
  runs: (id: string) => [...operatorsAutomationQueryKeys.all, "runs", id] as const,
};

export type SaveOperatorsAutomation = {
  creationRequestId?: string;
  expectedVersion?: number;
  id?: string;
  name: string;
  prompt: string;
  schedule: OperatorsAutomationSchedule;
  target: OperatorsAutomationTarget;
};

async function automationFetch<T>(input: {
  fallbackError: string;
  init?: RequestInit;
  path?: string;
}): Promise<T> {
  const response = await fetch(
    `${getOperatorsHttpOrigin()}/api/v1/automations${input.path ?? ""}`,
    { credentials: "include", ...input.init },
  );
  if (response.status === 401) {
    throwOperatorsAuthExpired();
  }
  if (response.status === 403) {
    throw new OperatorsForbiddenError(
      await readOperatorsAuthErrorMessage(response).catch(() => "Automation access denied."),
    );
  }
  if (!response.ok) {
    throw new Error(await readOperatorsAuthErrorMessage(response).catch(() => input.fallbackError));
  }
  return (await response.json()) as T;
}

export function operatorsAutomationsQueryOptions() {
  return queryOptions({
    queryKey: operatorsAutomationQueryKeys.list(),
    queryFn: async () =>
      automationFetch<OperatorsAutomationsResponse>({
        fallbackError: "Could not load automations.",
      }),
    staleTime: 3_000,
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
  });
}

export function saveOperatorsAutomationMutationOptions(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: async (variables: SaveOperatorsAutomation) => {
      const body = {
        creationRequestId: variables.creationRequestId,
        expectedVersion: variables.expectedVersion,
        name: variables.name,
        prompt: variables.prompt,
        schedule: variables.schedule,
        target: variables.target,
      };
      return await automationFetch<OperatorsAutomation>({
        fallbackError: variables.id
          ? "Could not update automation."
          : "Could not create automation.",
        init: {
          body: JSON.stringify(body),
          headers: { "content-type": "application/json" },
          method: variables.id ? "PATCH" : "POST",
        },
        path: variables.id ? `/${variables.id}` : "",
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: operatorsAutomationQueryKeys.all });
    },
  });
}

export function setOperatorsAutomationStatusMutationOptions(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: async (variables: {
      expectedVersion: number;
      id: string;
      status: "active" | "paused" | "archived";
    }) =>
      automationFetch<OperatorsAutomation>({
        fallbackError: "Could not update automation status.",
        init: {
          body: JSON.stringify({
            expectedVersion: variables.expectedVersion,
            status: variables.status,
          }),
          headers: { "content-type": "application/json" },
          method: "PATCH",
        },
        path: `/${variables.id}`,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: operatorsAutomationQueryKeys.all });
    },
  });
}

export function runOperatorsAutomationNowMutationOptions(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: async (id: string) =>
      automationFetch<unknown>({
        fallbackError: "Could not start automation.",
        init: { method: "POST" },
        path: `/${id}/run-now`,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: operatorsAutomationQueryKeys.all });
    },
  });
}
